import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Calendar, ArrowLeft, Phone, ChevronRight, HelpCircle, BookOpen } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { BLOG_POSTS } from "../lib/blog";
import { COMPANY } from "../lib/data";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

// Simple parser : **gras**, [texte](url)
function renderInline(text) {
  // Étape 1 : split par gras
  const boldSplit = text.split(/\*\*(.+?)\*\*/g);
  return boldSplit.map((chunk, i) => {
    if (i % 2 === 1) return <strong key={i} className="text-ink font-semibold">{chunk}</strong>;
    // Étape 2 : sur les segments non-gras, gérer les liens [txt](url)
    const parts = [];
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIdx = 0;
    let m;
    let idx = 0;
    while ((m = linkRegex.exec(chunk)) !== null) {
      if (m.index > lastIdx) parts.push(<span key={`t-${i}-${idx++}`}>{chunk.slice(lastIdx, m.index)}</span>);
      parts.push(
        <Link key={`l-${i}-${idx++}`} to={m[2]} className="text-brand font-medium hover:underline">
          {m[1]}
        </Link>
      );
      lastIdx = m.index + m[0].length;
    }
    if (lastIdx < chunk.length) parts.push(<span key={`t-${i}-${idx++}`}>{chunk.slice(lastIdx)}</span>);
    return parts.length ? <span key={i}>{parts}</span> : <span key={i}>{chunk}</span>;
  });
}

function renderBody(body) {
  const blocks = body.split(/\n\n+/);
  const out = [];
  blocks.forEach((block, bi) => {
    // Détection : le block contient-il des lignes en "- " ?
    const lines = block.split("\n");
    const bulletIdx = lines.findIndex((l) => l.startsWith("- "));
    if (bulletIdx >= 0) {
      // Rendu de la partie intro (avant les puces), s'il y en a
      const beforeLines = lines.slice(0, bulletIdx).join("\n").trim();
      if (beforeLines) {
        out.push(
          <p key={`p-${bi}`} className="text-ink-soft text-base md:text-[17px] leading-relaxed my-4">
            {renderInline(beforeLines)}
          </p>
        );
      }
      const items = lines.slice(bulletIdx).filter((l) => l.startsWith("- ")).map((l) => l.slice(2));
      out.push(
        <ul key={`ul-${bi}`} className="my-5 space-y-2.5">
          {items.map((it, ii) => (
            <li key={ii} className="flex items-start gap-2.5 text-ink-soft leading-relaxed">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand shrink-0" />
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ul>
      );
    } else {
      out.push(
        <p key={`p-${bi}`} className="text-ink-soft text-base md:text-[17px] leading-relaxed my-4">
          {renderInline(block)}
        </p>
      );
    }
  });
  return out;
}

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useEffect(() => { if (!post) navigate("/blog"); }, [post, navigate]);
  if (!post) return null;

  const related = BLOG_POSTS.filter((p) => p.slug !== slug && p.category === post.category).slice(0, 2);
  const others = BLOG_POSTS.filter((p) => p.slug !== slug && p.category !== post.category).slice(0, 1);
  const suggestions = [...related, ...others].slice(0, 3);

  // JSON-LD Article schema
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image,
    "datePublished": post.date,
    "author": { "@type": "Organization", "name": COMPANY.name },
    "publisher": {
      "@type": "Organization",
      "name": COMPANY.name,
      "logo": { "@type": "ImageObject", "url": "/logo.png" },
    },
    "keywords": post.keywords.join(", "),
  };

  return (
    <main data-testid={`blog-article-${slug}`} className="bg-white">
      <Seo
        path={`/blog/${slug}`}
        title={post.metaTitle}
        description={post.excerpt}
        image={post.image}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Hero */}
      <section className="pt-28 md:pt-32 pb-10 md:pb-14 bg-slate-50 border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-1.5 text-ink-soft hover:text-brand text-sm font-medium transition-colors">
            <ArrowLeft className="h-4 w-4" /> Retour au blog
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-2 text-sm">
            <span className="chip">{post.category}</span>
            <span className="flex items-center gap-1 text-ink-soft"><Calendar className="h-3.5 w-3.5" /> {formatDate(post.date)}</span>
            <span className="flex items-center gap-1 text-ink-soft"><Clock className="h-3.5 w-3.5" /> {post.readingTime} min de lecture</span>
          </div>
          <h1 className="mt-4 font-display font-extrabold text-ink text-3xl md:text-5xl leading-[1.1]">
            {post.title}
          </h1>
          <p className="mt-5 text-lg text-ink-soft leading-relaxed">{post.excerpt}</p>
        </div>
      </section>

      {/* Image */}
      <div className="max-w-4xl mx-auto px-5 md:px-8 -mt-2">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl shadow-slate-200/60 aspect-[16/9]"
        >
          <img src={post.image} alt={post.title} fetchPriority="high" className="h-full w-full object-cover" />
        </motion.div>
      </div>

      {/* Content */}
      <section className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <article className="prose-none">
            {/* Intro */}
            <Reveal>
              <p className="text-ink text-lg md:text-xl leading-relaxed font-medium border-l-4 border-brand pl-5 my-6">
                {renderInline(post.intro)}
              </p>
            </Reveal>

            {/* Sections */}
            {post.sections.map((s, i) => (
              <Reveal key={i}>
                <div>
                  <h2 className="font-display font-extrabold text-ink text-2xl md:text-3xl mt-12 mb-4">{s.h2}</h2>
                  {renderBody(s.body)}
                </div>
              </Reveal>
            ))}

            {/* CTA in-line */}
            <div className="my-14 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 text-center">
              <p className="overline text-brand">Besoin d&apos;un artisan ?</p>
              <h3 className="mt-2 font-display font-extrabold text-2xl md:text-3xl">
                Intervention 24 h/24 en Île-de-France
              </h3>
              <p className="mt-3 text-white/70 max-w-md mx-auto">
                Un {post.category.toLowerCase()} certifié chez vous en moins d&apos;une heure, devis annoncé avant intervention.
              </p>
              <a href={COMPANY.phoneHref} data-testid="article-call-btn" className="btn-primary mt-6">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
            </div>

            {/* FAQ */}
            <h2 className="font-display font-extrabold text-ink text-2xl md:text-3xl mt-12 mb-4 flex items-center gap-2">
              <HelpCircle className="h-6 w-6 text-brand" /> Questions fréquentes
            </h2>
            <Accordion type="single" collapsible className="w-full space-y-3">
              {post.faq.map((item, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="card-soft border border-slate-200 px-6 hover:translate-y-0">
                  <AccordionTrigger className="font-display font-bold text-ink text-base md:text-lg text-left hover:no-underline py-5 [&>svg]:text-brand">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-ink-soft text-base leading-relaxed pb-5">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Internal links */}
            <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <h3 className="font-display font-bold text-ink text-lg mb-3 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-brand" /> Pour aller plus loin
              </h3>
              <ul className="space-y-2">
                {post.internalLinks.map((l) => (
                  <li key={l.to} className="flex items-center gap-1.5 text-brand hover:text-brand-dark">
                    <ChevronRight className="h-4 w-4" />
                    <Link to={l.to} className="font-medium hover:underline">Découvrir {l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>
      </section>

      {/* Related posts */}
      {suggestions.length > 0 && (
        <section className="py-16 md:py-20 bg-slate-50 border-t border-slate-200">
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <h2 className="font-display font-extrabold text-ink text-2xl md:text-3xl text-center">
              À lire également
            </h2>
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {suggestions.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="card-soft overflow-hidden hover:translate-y-0 group block"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <span className="chip !py-0.5 !px-2 text-xs">{p.category}</span>
                    <h3 className="mt-3 font-display font-bold text-ink text-lg leading-tight group-hover:text-brand transition-colors">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
